/**
 * Line icons for the Long Night, drawn rather than typed: Unicode symbols such as ✝ or ⛩ turn into
 * colored emoji on phones. 24×24, stroked in the current color.
 */

export type NightIconName =
  // Omens
  | 'candle'
  | 'gilt-tile'
  | 'salt-circle'
  | 'pillar'
  | 'well'
  | 'ink-pool'
  | 'chalk-x'
  | 'thorns'
  | 'altar'
  | 'trapdoor'
  | 'lost-ring'
  | 'draft'
  | 'mimic'
  | 'unknown'
  // Kinds of things
  | 'superstition'
  | 'curio'
  | 'appraisal'
  | 'card'
  | 'carving'
  | 'bargain'
  | 'route'
  | 'omen'
  | 'fixture'
  | 'lot'
  | 'trick'
  | 'detour'
  | 'favor'
  | 'pawn'
  // Interface
  | 'brass'
  | 'ledger'
  | 'bell'
  | 'lock'
  | 'info'
  | 'arrow-left'
  | 'arrow-right'
  | 'check';

const PATHS: Readonly<Record<NightIconName, string>> = {
  candle: 'M12 3c1.8 2 2.2 3.6 0 5.5C9.8 6.6 10.2 5 12 3z M9 10h6v10H9z M7 20h10',
  'gilt-tile': 'M12 3l8 9-8 9-8-9z M12 8l3.5 4-3.5 4-3.5-4z',
  'salt-circle': 'M12 4a8 8 0 1 0 0.01 0z M12 8a4 4 0 1 0 0.01 0z',
  pillar: 'M6 4h12v3H6z M8 7v11 M16 7v11 M12 7v11 M5 18h14v2H5z',
  well: 'M12 5a7 7 0 1 0 0.01 0z M12 9a3 3 0 1 0 0.01 0z',
  'ink-pool': 'M12 3c3 5 6 8 6 11a6 6 0 0 1-12 0c0-3 3-6 6-11z',
  // A chalked 3 in a ring: the number that scores on it (no crosses on the board).
  'chalk-x': 'M12 3a9 9 0 1 0 0.01 0z M9.5 7.5h5l-2.6 3.4a2.7 2.7 0 1 1-2.7 3.6',
  thorns: 'M4 18c4-1 6-5 8-12 2 7 4 11 8 12 M8 13l-3-1 M16 13l3-1 M12 9V5',
  altar: 'M5 20h14 M7 20V12h10v8 M5 12h14 M9 8l3-4 3 4',
  trapdoor: 'M4 5h16v14H4z M4 12h16 M9 8.5h6',
  'lost-ring': 'M12 7a6 6 0 1 0 0.01 0z M10 4h4l-2 3z',
  draft: 'M3 9h11a3 3 0 1 0-3-3 M3 14h15a3 3 0 1 1-3 3 M3 19h7',
  mimic: 'M3 12c3-5 15-5 18 0-3 5-15 5-18 0z M12 9.5a2.5 2.5 0 1 0 0.01 0z',
  unknown: 'M9 9a3 3 0 1 1 4 2.8c-.7.3-1 1-1 1.7V15 M12 18.5v.5',
  superstition: 'M12 3l2.4 5.6L20 9.3l-4.3 3.9 1.2 5.8L12 16l-4.9 3 1.2-5.8L4 9.3l5.6-.7z',
  curio: 'M9 3h6l-1 4h-4z M7 7h10l2 13H5z',
  appraisal: 'M12 4a8 8 0 1 0 0.01 0z M12 8v4l3 2',
  card: 'M7 3h10a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z M9 8h2',
  carving: 'M4 20l4-1L19 8l-3-3L5 16z M14 7l3 3',
  bargain: 'M12 4v16 M5 8h14 M5 8l-2 6h4z M19 8l-2 6h4z M8 20h8',
  route: 'M6 19c0-6 12-4 12-10a4 4 0 0 0-8 0 M6 19a1.5 1.5 0 1 0 0.01 0z',
  omen: 'M12 3a9 9 0 1 0 0.01 0z M12 7v6 M12 16v.5',
  fixture: 'M4 10l8-6 8 6 M6 10v10h12V10 M10 20v-5h4v5',
  lot: 'M4 8l8-4 8 4v9l-8 4-8-4z M4 8l8 4 8-4 M12 12v9',
  trick: 'M12 2v6 M12 16v6 M2 12h6 M16 12h6 M7 7l2 2 M15 15l2 2 M17 7l-2 2 M9 15l-2 2',
  detour: 'M6 20V9a5 5 0 0 1 10 0v2 M13 8l3 3 3-3',
  favor: 'M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.5A4 4 0 0 1 19 10c0 5.5-7 10-7 10z',
  pawn: 'M12 4a3 3 0 1 0 0.01 0z M9 20l1.5-8h3L15 20z M7 20h10',
  // The brass sign ¤, as the prices print it.
  brass: 'M12 6.5a5.5 5.5 0 1 0 0.01 0z M5 5l3.1 3.1 M19 5l-3.1 3.1 M5 19l3.1-3.1 M19 19l-3.1-3.1',
  ledger: 'M6 3h11a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H6z M6 3v18 M9 8h6 M9 12h6',
  bell: 'M6 16V11a6 6 0 0 1 12 0v5l2 2H4z M10 20a2 2 0 0 0 4 0',
  lock: 'M6 11h12v9H6z M9 11V8a3 3 0 0 1 6 0v3',
  info: 'M12 3a9 9 0 1 0 0.01 0z M12 11v6 M12 7.5v.5',
  'arrow-left': 'M15 5l-7 7 7 7',
  'arrow-right': 'M9 5l7 7-7 7',
  check: 'M5 12l5 5 9-10',
};

export function NightIcon({
  name,
  size = 20,
  className = '',
}: {
  name: NightIconName;
  size?: number;
  className?: string;
}) {
  return (
    <svg
      className={`night-icon ${className}`}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}

/** The icon for an Omen by kind (a Shrouded one is unknown). */
export const omenIcon = (kind: string): NightIconName =>
  (kind in PATHS ? kind : 'unknown') as NightIconName;
